import React from "react";
import { AlertCircle, AlertTriangle } from "lucide-react";

/** Amber pill shown for any existing payroll that will be overwritten */
const ConflictBadge = ({ conflict, currentTheme, isAr }) => {
  if (!conflict) {
    return (
      <span style={{ color: currentTheme.colors.success, fontWeight: "600", fontSize: "12px" }}>
        ✅ {isAr ? "جديد" : "New"}
      </span>
    );
  }

  const cfg = {
    PAID: {
      bg: "rgba(239,68,68,0.12)",
      border: "rgba(239,68,68,0.4)",
      color: "#ef4444",
      icon: "🔴",
      label: isAr ? "مدفوع" : "PAID",
    },
    HAS_REPAYMENT: {
      bg: "rgba(245,158,11,0.12)",
      border: "rgba(245,158,11,0.4)",
      color: "#f59e0b",
      icon: "🟡",
      label: isAr ? "له سلفة" : "Has repayment",
    },
    PAID_AND_REPAYMENT: {
      bg: "rgba(239,68,68,0.12)",
      border: "rgba(239,68,68,0.4)",
      color: "#ef4444",
      icon: "🔴",
      label: isAr ? "مدفوع + سلفة" : "Paid + repayment",
    },
    STANDARD: {
      bg: "rgba(245,158,11,0.09)",
      border: "rgba(245,158,11,0.35)",
      color: "#b45309",
      icon: "🟠",
      label: isAr ? "سيُستبدل" : "Will overwrite",
    },
  };

  const c = cfg[conflict.conflictType] || cfg.STANDARD;
  const importedOn = conflict.importedAt
    ? new Date(conflict.importedAt).toLocaleDateString(isAr ? "ar-EG" : "en-GB", {
        day: "2-digit",
        month: "short",
      })
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          padding: "3px 9px",
          borderRadius: "20px",
          fontSize: "11px",
          fontWeight: "700",
          background: c.bg,
          border: `1px solid ${c.border}`,
          color: c.color,
          whiteSpace: "nowrap",
        }}
      >
        {c.icon} {c.label}
      </span>
      {importedOn && (
        <span style={{ fontSize: "11px", color: currentTheme.colors.textMuted }}>
          {isAr ? `تم الاستيراد: ${importedOn}` : `imported ${importedOn}`}
        </span>
      )}
    </div>
  );
};

const thStyle = (currentTheme) => ({
  padding: "12px",
  textAlign: "start",
  fontWeight: "600",
  fontSize: "13px",
  color: currentTheme.colors.textPrimary,
  background: currentTheme.colors.inputBackground,
  borderBottom: `1px solid ${currentTheme.colors.border}`,
  position: "sticky",
  top: 0,
});

const PreviewStep = ({
  t,
  currentTheme,
  modalStyles,
  importData,
  conflicts,           // Map<clientCode, conflictInfo>
  personalChanges,
  applyPersonalUpdates,
  setApplyPersonalUpdates,
  showPersonalChanges,
  setShowPersonalChanges,
  isAr,
}) => {
  const validRows   = importData?.filter((row) => row.isValid) || [];
  const invalidRows = importData?.filter((row) => !row.isValid) || [];

  // Total conflicting valid rows (for stat card)
  const conflictCount = validRows.filter((r) => conflicts?.[r.data.clientCode]).length;

  return (
    <div>
      {/* ── Responsibility banner ── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
          padding: "12px 16px",
          borderRadius: "10px",
          background: "rgba(245,158,11,0.09)",
          border: "1px solid rgba(245,158,11,0.35)",
          marginBottom: "16px",
        }}
      >
        <AlertTriangle size={17} color="#f59e0b" style={{ flexShrink: 0, marginTop: "2px" }} />
        <span style={{ fontSize: "13px", color: currentTheme.colors.textSecondary, lineHeight: "1.6" }}>
          {isAr
            ? "جميع القيم في هذا الاستيراد مأخوذة من ملفك. النظام لن يُعيد حساب الضرائب أو حصة صندوق الشهداء أو الراتب الإجمالي/الصافي للرواتب المستوردة."
            : "All values in this import are taken from your file. The system will not recalculate taxes, Martyrs' Fund, or gross/net figures for imported records."}
        </span>
      </div>

      {/* ── Stats ── */}
      <div style={modalStyles.statsGrid}>
        <div style={modalStyles.statCard}>
          <div style={{ ...modalStyles.statValue, color: currentTheme.colors.primary }}>
            {importData?.length || 0}
          </div>
          <div style={modalStyles.statLabel}>{t("totalRows")}</div>
        </div>
        <div style={modalStyles.statCard}>
          <div style={{ ...modalStyles.statValue, color: currentTheme.colors.success }}>
            {validRows.length}
          </div>
          <div style={modalStyles.statLabel}>{t("validRows")}</div>
        </div>
        <div style={modalStyles.statCard}>
          <div style={{ ...modalStyles.statValue, color: currentTheme.colors.danger }}>
            {invalidRows.length}
          </div>
          <div style={modalStyles.statLabel}>{t("errorRows")}</div>
        </div>
        {conflictCount > 0 && (
          <div style={modalStyles.statCard}>
            <div style={{ ...modalStyles.statValue, color: "#f59e0b" }}>
              {conflictCount}
            </div>
            <div style={modalStyles.statLabel}>
              {isAr ? "تعارضات" : "Conflicts"}
            </div>
          </div>
        )}
      </div>

      {/* ── Personal info diff panel (existing) ── */}
      {personalChanges?.length > 0 && (
        <div
          style={{
            margin: "12px 0",
            padding: "12px 14px",
            borderRadius: 10,
            border: `1px solid ${currentTheme.colors.warning}`,
            background: "rgba(245,158,11,0.07)",
            color: currentTheme.colors.textPrimary,
            fontSize: 13,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <strong>
              {isAr
                ? `البيانات الشخصية في الملف مختلفة عن بيانات ${personalChanges.length} موظف`
                : `Personal info in the file differs for ${personalChanges.length} employee(s)`}
            </strong>
            <button
              type="button"
              onClick={() => setShowPersonalChanges((v) => !v)}
              style={{ background: "transparent", border: "none", color: currentTheme.colors.primary, cursor: "pointer", fontWeight: 600 }}
            >
              {showPersonalChanges ? (isAr ? "إخفاء" : "Hide") : isAr ? "عرض التغييرات" : "Review changes"}
            </button>
          </div>
          {showPersonalChanges && (
            <div style={{ marginTop: 8, maxHeight: 200, overflowY: "auto" }}>
              {personalChanges.map((emp) => (
                <div key={emp.clientCode} style={{ padding: "6px 0", borderTop: `1px solid ${currentTheme.colors.borderLight}` }}>
                  <div style={{ fontWeight: 600 }}>{emp.employeeName} ({emp.clientCode})</div>
                  {emp.changes.map((c) => (
                    <div key={c.field} style={{ color: currentTheme.colors.textSecondary }}>
                      {c.label}: <span style={{ textDecoration: "line-through" }}>{c.current ?? "—"}</span> → <strong>{c.incoming}</strong>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={applyPersonalUpdates}
              onChange={(e) => setApplyPersonalUpdates(e.target.checked)}
            />
            {isAr
              ? "حدّث بيانات هؤلاء الموظفين أيضًا (بدون المرتب)"
              : "Also update these employees' info (salary is never changed)"}
          </label>
          {!applyPersonalUpdates && (
            <div style={{ marginTop: 4, color: currentTheme.colors.textSecondary }}>
              {isAr ? "بدون التحديد: يتم استيراد الرواتب فقط وتبقى بيانات الموظفين كما هي." : "Unticked: only the payroll is imported; employee records stay as they are."}
            </div>
          )}
        </div>
      )}

      {/* ── Preview table ── */}
      <div style={modalStyles.tableContainer}>
        <table style={modalStyles.table}>
          <thead>
            <tr>
              <th style={thStyle(currentTheme)}>{t("row")}</th>
              <th style={thStyle(currentTheme)}>{t("code")}</th>
              <th style={thStyle(currentTheme)}>{t("name")}</th>
              <th style={thStyle(currentTheme)}>{t("period")}</th>
              <th style={thStyle(currentTheme)}>{isAr ? "تعارض" : "Conflict"}</th>
              <th style={thStyle(currentTheme)}>{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            {importData?.map((row, index) => {
              const conflict = conflicts?.[row.data.clientCode] || null;
              const rowBg = !row.isValid
                ? `${currentTheme.colors.danger}08`
                : conflict
                ? "rgba(245,158,11,0.04)"
                : "transparent";
              return (
                <tr key={index} style={{ background: rowBg }}>
                  <td style={{ padding: "12px", borderBottom: `1px solid ${currentTheme.colors.border}`, color: currentTheme.colors.textSecondary, fontSize: "13px" }}>
                    {row.rowNumber}
                  </td>
                  <td style={{ padding: "12px", borderBottom: `1px solid ${currentTheme.colors.border}`, color: currentTheme.colors.textPrimary, fontSize: "13px", fontWeight: "500" }}>
                    {row.data.clientCode || "N/A"}
                  </td>
                  <td style={{ padding: "12px", borderBottom: `1px solid ${currentTheme.colors.border}`, color: currentTheme.colors.textSecondary, fontSize: "13px" }}>
                    {row.data.employeeName || "N/A"}
                  </td>
                  <td style={{ padding: "12px", borderBottom: `1px solid ${currentTheme.colors.border}`, color: currentTheme.colors.textSecondary, fontSize: "13px" }}>
                    {row.data.year}/{row.data.month}
                  </td>
                  <td style={{ padding: "12px", borderBottom: `1px solid ${currentTheme.colors.border}`, fontSize: "13px" }}>
                    <ConflictBadge conflict={conflict} currentTheme={currentTheme} isAr={isAr} />
                  </td>
                  <td style={{ padding: "12px", borderBottom: `1px solid ${currentTheme.colors.border}`, fontSize: "13px" }}>
                    {row.isValid ? (
                      <span style={{ color: currentTheme.colors.success, fontWeight: "600" }}>✓ {t("valid")}</span>
                    ) : (
                      <span style={{ color: currentTheme.colors.danger, fontWeight: "500", fontSize: "12px" }}>
                        {row.errors.join(", ")}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {invalidRows.length > 0 && (
        <div style={modalStyles.warningBox}>
          <AlertCircle size={18} style={{ marginInlineEnd: "8px", verticalAlign: "middle" }} />
          <span style={{ fontSize: "14px", fontWeight: "500" }}>
            {t("errorsWillBeSkipped").replace("{count}", invalidRows.length)}
          </span>
        </div>
      )}
    </div>
  );
};

export default PreviewStep;